import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { Reveal } from "@/components/site/Reveal";
import { useLanguage } from "@/context/LanguageContext";
import { MEDIA } from "@/lib/collection";

const ART = "https://customer-assets-jt897jd0.emergentagent.net/job_the-x-goes-here/artifacts/";

const CASES = [
  {
    id: "monumental-gallery",
    image: MEDIA.institution,
    alt: "A monumental blue painted X on the wall of a skylit museum gallery",
    type: { en: "Foundation · Gallery", fr: "Fondation · Galerie" },
    surface: { en: "Mural — approx. 6 × 4 m", fr: "Fresque — environ 6 × 4 m" },
    location: { en: "Paris", fr: "Paris" },
    concept: true,
    text: {
      en: "At monumental scale, the empty cross stops being a placeholder and becomes the statement itself — the museum wall asking for the masterpiece it doesn't have.",
      fr: "À l'échelle monumentale, la croix vide cesse d'être un emplacement réservé pour devenir le manifeste lui-même — le mur du musée réclame le chef-d'œuvre qu'il n'a pas.",
    },
  },
  {
    id: "haussmann-entry",
    image: ART + "1clnft4w_ChatGPT%20Image%20Aug%2028%2C%202026%2C%2005_12_36%20PM.png",
    alt: "A giant black X painted directly on the wall of a Haussmann entry",
    type: { en: "Private home", fr: "Résidence privée" },
    surface: { en: "Mural — approx. 3 × 3 m", fr: "Fresque — environ 3 × 3 m" },
    location: { en: "Paris 9e", fr: "Paris 9e" },
    concept: true,
    text: {
      en: "A Haussmann entry, its mouldings intact, interrupted by a single hand-painted cross. The Picasso stays imaginary — the room does the dreaming.",
      fr: "Une entrée haussmannienne, ses moulures intactes, interrompue par une unique croix peinte à la main. Le Picasso reste imaginaire — c'est la pièce qui rêve.",
    },
  },
  {
    id: "collection-wall",
    image: ART + "zz68catd_ChatGPT%20Image%20Aug%2026%2C%202026%2C%2011_16_45%20AM.png",
    alt: "A six-print art wall of framed X prints above a linen sofa",
    type: { en: "Private collection", fr: "Collection privée" },
    surface: { en: "6 framed prints — 50 × 70 cm", fr: "6 tirages encadrés — 50 × 70 cm" },
    location: { en: "Île-de-France", fr: "Île-de-France" },
    concept: true,
    text: {
      en: "Six absences hung as one collection — Newman, Picasso, Basquiat, Warhol, Pollock, Rothko. A wall that reads like a library of everything you almost own.",
      fr: "Six absences accrochées comme une seule collection — Newman, Picasso, Basquiat, Warhol, Pollock, Rothko. Un mur qui se lit comme la bibliothèque de tout ce que l'on possède presque.",
    },
  },
];

export default function Exhibition() {
  const { t, lang } = useLanguage();

  return (
    <div className="bg-[#faf8f5] min-h-screen">
      <Nav solid />
      <main className="pt-24 md:pt-32 pb-24">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
            {t("exh.kicker")}
          </span>
          <h1 className="mt-6 max-w-3xl font-display text-4xl md:text-6xl leading-[1.02] tracking-tight text-[#111111]">
            {t("exh.title.a")}
            <span className="italic">{t("exh.title.i")}</span>
            {t("exh.title.b")}
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-[#111111]/70">
            {t("exh.intro")}
          </p>

          <div className="mt-16 space-y-20 md:space-y-28">
            {CASES.map((c, i) => (
              <Reveal key={c.id} delay={0.05}>
                <article
                  data-testid={`exh-case-${c.id}`}
                  className={`grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-14 items-end ${
                    i % 2 === 1 ? "" : ""
                  }`}
                >
                  <div className={`lg:col-span-8 ${i % 2 === 1 ? "lg:order-2" : ""}`}>
                    <div className="border gallery-line overflow-hidden bg-white">
                      <img
                        src={c.image}
                        alt={c.alt}
                        className="w-full aspect-[16/10] object-cover"
                        loading="lazy"
                      />
                    </div>
                  </div>
                  <div className={`lg:col-span-4 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <div className="flex items-center gap-3">
                      <span className="font-display italic text-3xl text-[#a89f8e]">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      {c.concept && (
                        <span className="border border-[#111111]/25 px-2.5 py-1 text-[10px] uppercase tracking-[0.18em] text-[#111111]/60">
                          {t("exh.conceptVisual")}
                        </span>
                      )}
                    </div>
                    <dl className="mt-6 space-y-2 text-sm font-light text-[#111111]/70">
                      <div className="flex gap-3">
                        <dt className="w-20 shrink-0 uppercase tracking-[0.15em] text-[11px] text-[#a89f8e] pt-0.5">
                          {t("exh.type")}
                        </dt>
                        <dd>{c.type[lang] || c.type.en}</dd>
                      </div>
                      <div className="flex gap-3">
                        <dt className="w-20 shrink-0 uppercase tracking-[0.15em] text-[11px] text-[#a89f8e] pt-0.5">
                          {t("exh.format")}
                        </dt>
                        <dd>{c.surface[lang] || c.surface.en}</dd>
                      </div>
                      <div className="flex gap-3">
                        <dt className="w-20 shrink-0 uppercase tracking-[0.15em] text-[11px] text-[#a89f8e] pt-0.5">
                          {t("exh.location")}
                        </dt>
                        <dd>{c.location[lang] || c.location.en}</dd>
                      </div>
                    </dl>
                    <p className="mt-5 text-sm md:text-base font-light leading-relaxed text-[#111111]/70">
                      {c.text[lang] || c.text.en}
                    </p>
                    <Link
                      to="/commission"
                      data-testid={`exh-commission-${c.id}`}
                      className="link-underline mt-6 inline-block text-sm font-light text-[#111111]"
                    >
                      {t("exh.cta")}
                    </Link>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {/* Press & collaborations — reserved */}
          <div className="mt-28 border-t gallery-line pt-14" data-testid="exh-press">
            <span className="text-xs uppercase tracking-[0.3em] text-[#a89f8e]">
              {t("exh.press")}
            </span>
            <p className="mt-6 max-w-xl text-sm font-light leading-relaxed text-[#111111]/50">
              {t("exh.pressBody")}
            </p>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
